import React from 'react';
import { XMarkIcon, StarIcon, CalendarDaysIcon, UserIcon, BriefcaseIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

const ReviewDetailModal = ({ review, isOpen, onClose }) => {
  if (!isOpen || !review) return null;

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => {
      if (i < Math.floor(rating)) {
        return <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" />;
      } else {
        return <StarIcon key={i} className="h-5 w-5 text-gray-300" />;
      }
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* 배경 오버레이 */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* 모달 컨테이너 */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
        
        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {/* 헤더 */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">리뷰 상세보기</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* 평점 */}
          <div className="flex items-center gap-2 mb-4">
            {renderStars(review.rating)}
            <span className="text-2xl font-bold text-gray-900">{review.rating}.0</span>
          </div>

          {/* 프로젝트 정보 */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <BriefcaseIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">프로젝트</span>
                <span className="text-sm text-gray-900">{review.project_title}</span>
              </div>
              <div className="flex items-center gap-2">
                <UserIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">고객</span>
                <span className="text-sm text-gray-900">{review.customer_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">작성일</span>
                <span className="text-sm text-gray-900">
                  {format(new Date(review.created_at), 'yyyy년 MM월 dd일', { locale: ko })}
                </span>
              </div>
            </div>
          </div>

          {/* 리뷰 내용 */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-2">리뷰 내용</h4>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              {review.comment ? (
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{review.comment}</p>
              ) : (
                <p className="text-sm text-gray-400 italic">작성된 리뷰 내용이 없습니다.</p>
              )}
            </div>
          </div>

          {/* 항목별 평점 (있는 경우) */}
          {review.details && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">상세 평가</h4>
              <div className="space-y-2">
                {Object.entries(review.details).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{key}</span>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <StarIconSolid
                          key={i}
                          className={`h-3 w-3 ${
                            i < value ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 닫기 버튼 */}
          <div className="mt-5 sm:mt-6">
            <button
              type="button"
              className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
              onClick={onClose}
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewDetailModal;